import { useCallback, useRef } from 'react'

import { type FormikProps } from 'formik'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router'

import { toast } from '@webapp/components/Notification'
import { makeSelectUser } from '@webapp/core/stores/global'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { type ExpenseReportFormSchema } from '@webapp/modules/expense/components/CreateExpenseReportForm'
import { useResetExpenseRequestPresenter } from '@webapp/modules/expense/features/reset-expense-request'
import { useSaveExpenseDraftPresenter } from '@webapp/modules/expense/features/save-expense-draft'
import { useSubmitExpenseReportPresenter } from '@webapp/modules/expense/features/submit-expense-report'
import { useApproverResolution } from './hooks/useApproverResolution'
import { useBudgetConfirmation } from './hooks/useBudgetConfirmation'
import { useExpenseFormInitialization } from './hooks/useExpenseFormInitialization'
import messages from './messages'

export const useExpenseRequestView = () => {
  const intl = useProcurifyIntl()
  const history = useHistory()
  const { url } = useAppRoutes()
  const currentUser = useSelector(makeSelectUser())
  const currentUserId = currentUser?.id

  const formikRef = useRef<FormikProps<ExpenseReportFormSchema> | null>(null)

  const {
    formData,
    initialValues,
    isDraft,
    draftId,
    formKey,
    baseCurrency,
    isFormDataLoading,
    setDraftId,
    incrementFormKey,
  } = useExpenseFormInitialization()

  const submitReportRef = useRef<
    (
      values: ExpenseReportFormSchema,
      shouldSkipBudgetCheck: boolean
    ) => Promise<void>
  >(() => Promise.resolve())

  const budgetConfirm = useBudgetConfirmation(submitReportRef)

  const {
    commands: { submitReport },
    states: { isLoading: isSubmitting },
  } = useSubmitExpenseReportPresenter({
    onSuccess: useCallback(
      (selfApproved: boolean) => {
        toast.success({
          title: intl.formatMessage(messages.submitSuccessTitle),
        })
        if (selfApproved) {
          history.push(url('ExpenseStatusPage', { status: 'approved' }))
        } else {
          history.push(url('ExpenseStatusPage', { status: 'pending' }))
        }
      },
      [history, intl, url]
    ),
    onBudgetError: budgetConfirm.onBudgetError,
  })
  submitReportRef.current = submitReport

  const {
    commands: { saveDraft },
    states: { isLoading: isSavingDraft },
  } = useSaveExpenseDraftPresenter({
    onSuccess: useCallback(
      (id: number) => {
        setDraftId(id)
        // Propagate the new draft id into Formik so a subsequent Submit takes
        // the PUT (update) branch in SubmitExpenseReportUseCase instead of
        // POSTing a second expense and orphaning the draft.
        formikRef.current?.setFieldValue('id', id)
        toast.success({
          title: intl.formatMessage(messages.draftSavedTitle),
        })
      },
      [intl, setDraftId]
    ),
  })

  const {
    commands: { resetForm },
    states: { isLoading: isResetting },
  } = useResetExpenseRequestPresenter({
    onReset: useCallback(() => {
      setDraftId(null)
      toast.success({
        title: intl.formatMessage(messages.resetSuccessTitle),
      })
      incrementFormKey()
    }, [intl, setDraftId, incrementFormKey]),
  })

  const approver = useApproverResolution(formikRef, formData)

  const handleSaveDraft = useCallback(
    (values: ExpenseReportFormSchema) => {
      saveDraft(values)
    },
    [saveDraft]
  )

  const handleFormSubmit = useCallback(
    (values: ExpenseReportFormSchema) => {
      submitReport(values, false)
    },
    [submitReport]
  )

  const isLoading =
    isFormDataLoading || isSavingDraft || isSubmitting || isResetting

  return {
    data: {
      formData,
      initialValues,
      isDraft,
      draftId,
      formKey,
      approverOptions: approver.approverOptions,
      isBudgetConfirmOpen: budgetConfirm.isBudgetConfirmOpen,
      baseCurrency,
    },
    states: {
      isFormDataLoading,
      isApproverLoading: approver.isApproverLoading,
      isLoading,
      userId: currentUserId,
    },
    methods: {
      onDepartmentChange: approver.onDepartmentChange,
      onAmountChange: approver.onAmountChange,
      onAccountCodesChange: approver.onAccountCodesChange,
      onSaveDraft: handleSaveDraft,
      onFormSubmit: handleFormSubmit,
      onReset: resetForm,
      onConfirmOverBudget: budgetConfirm.onConfirmOverBudget,
      onDismissBudgetConfirm: budgetConfirm.onDismissBudgetConfirm,
    },
    refs: {
      formikRef,
    },
  }
}
